import _defineProperty from "@babel/runtime/helpers/esm/defineProperty";
import app from 'flarum/common/app';
import Component from 'flarum/common/Component';
import LoadingIndicator from 'flarum/common/components/LoadingIndicator';
import KeyboardNavigatable from 'flarum/common/utils/KeyboardNavigatable';
import username from 'flarum/common/helpers/username';
import Icon from 'flarum/common/components/Icon';
import Group from 'flarum/common/models/Group';
import { getContrastTextColor } from '../utils/color';
/**
 * Reusable tag-style recipient picker: search-as-you-type over users and/or
 * groups, chip display of current selection, keyboard navigation.
 *
 * Data ownership stays with the parent (`recipients` / `onChange`); this
 * component only owns its own search UI state.
 */
export default class RecipientPicker extends Component {
  constructor() {
    super(...arguments);
    _defineProperty(this, "filter", '');
    _defineProperty(this, "focused", false);
    _defineProperty(this, "loadingResults", false);
    _defineProperty(this, "searchResults", []);
    _defineProperty(this, "searchIndex", 0);
    _defineProperty(this, "searchTimeout", -1);
    _defineProperty(this, "lastApiResults", []);
    _defineProperty(this, "navigator", new KeyboardNavigatable());
  }
  oninit(vnode) {
    super.oninit(vnode);
    this.navigator.when(event => event.key !== 'Tab' || !!this.filter).onUp(() => {
      if (this.searchIndex > 0) {
        this.searchIndex--;
        m.redraw();
      }
    }).onDown(() => {
      if (this.searchIndex < this.searchResults.length - 1) {
        this.searchIndex++;
        m.redraw();
      }
    }).onSelect(() => this.selectResult(this.searchResults[this.searchIndex])).onRemove(() => {
      const recipients = this.attrs.recipients.slice(0, -1);
      this.attrs.onChange(recipients);
      m.redraw();
    });
  }
  get searchUsers() {
    return this.attrs.searchUsers !== false;
  }
  get searchGroups() {
    return this.attrs.searchGroups !== false;
  }

  /** translationPrefix is expected to be e.g. 'huseyinfiliz-notificationhub.forum' */
  t(subKey, params) {
    return app.translator.trans("".concat(this.attrs.translationPrefix, ".").concat(subKey), params);
  }
  recipientLabel(recipient) {
    if (recipient.data.type === 'users') {
      return m("span", {
        className: "RecipientLabel"
      }, username(recipient));
    }
    if (recipient.data.type === 'groups') {
      const group = recipient;
      const textColor = getContrastTextColor(group.color());
      return m("span", {
        className: 'RecipientLabel' + (group.color() ? ' colored' : ''),
        style: group.color() ? {
          backgroundColor: group.color(),
          color: textColor || undefined
        } : {}
      }, group.icon() ? [Icon.component({
        name: group.icon()
      }), ' '] : null, group.namePlural());
    }
    return '[unknown]';
  }
  searchResultKind(recipient) {
    if (recipient.data.type === 'users') {
      return this.t('recipient_kinds.user');
    }
    if (recipient.data.type === 'groups') {
      return this.t('recipient_kinds.group');
    }
    return '[unknown]';
  }
  selectResult(result) {
    if (!result) {
      return;
    }
    this.attrs.onChange([...this.attrs.recipients, result]);
    this.filter = '';
    this.searchResults = [];
    m.redraw();
  }
  removeRecipient(index) {
    const recipients = this.attrs.recipients.slice();
    recipients.splice(index, 1);
    this.attrs.onChange(recipients);
    m.redraw();
  }
  performNewSearch() {
    this.searchIndex = 0;
    const query = this.filter.toLowerCase();
    this.buildSearchResults(query);
    clearTimeout(this.searchTimeout);
    if (query.length >= 3 && this.searchUsers) {
      this.searchTimeout = window.setTimeout(() => {
        this.loadingResults = true;
        m.redraw();
        app.store.find('users', {
          filter: {
            q: query
          },
          page: {
            limit: 5
          }
        }).then(results => {
          this.loadingResults = false;
          this.lastApiResults = results || [];
          this.buildSearchResults(query);
          m.redraw();
        });
      }, 250);
    }
  }
  buildSearchResults(query) {
    if (!query) {
      this.searchResults = [];
      return;
    }
    const results = [];
    if (this.searchGroups) {
      app.store.all('groups').forEach(group => {
        if (group.id() === Group.GUEST_ID) {
          return;
        }
        if (group.nameSingular().toLowerCase().indexOf(query) !== -1 || group.namePlural().toLowerCase().indexOf(query) !== -1) {
          results.push(group);
        }
      });
    }
    if (this.searchUsers) {
      this.lastApiResults.forEach(user => {
        if (user.username().toLowerCase().indexOf(query) !== -1) {
          results.push(user);
        }
      });
    }
    this.searchResults = results.filter(result => {
      return !this.attrs.recipients.some(recipient => recipient.data.type === result.data.type && recipient.id() === result.id());
    });
    m.redraw();
  }
  view() {
    const disabled = !!this.attrs.disabled;
    return m("div", {
      className: 'RecipientPicker RecipientsInput FormControl' + (this.focused ? ' focus' : '')
    }, m("span", {
      className: "RecipientsInput-selected"
    }, this.attrs.recipients.map((recipient, index) => m("span", {
      className: "RecipientsInput-recipient",
      onclick: () => !disabled && this.removeRecipient(index),
      title: this.searchResultKind(recipient)
    }, this.recipientLabel(recipient)))), m("input", {
      className: "FormControl",
      placeholder: this.attrs.placeholder || this.t('modal_notification.recipients_placeholder'),
      value: this.filter,
      disabled: disabled,
      oninput: event => {
        this.filter = event.target.value;
        this.performNewSearch();
      },
      onkeydown: this.navigator.navigate.bind(this.navigator),
      onfocus: () => {
        this.focused = true;
      },
      onblur: () => {
        this.focused = false;
      }
    }), this.loadingResults ? LoadingIndicator.component({
      size: 'small'
    }) : null, this.searchResults.length ? m('ul.Dropdown-menu.search-dropdown', this.searchResults.map((result, index) => m('li', {
      className: this.searchIndex === index ? 'active' : '',
      onmousedown: e => {
        e.preventDefault();
        this.selectResult(result);
      }
    }, m('button[type=button]', [m('span.SearchResultKind', this.searchResultKind(result)), this.recipientLabel(result)])))) : null);
  }
}
flarum.reg.add('huseyinfiliz-notificationhub', 'common/components/RecipientPicker', RecipientPicker);